import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import ReasonPromptModal from '@/components/admin/ReasonPromptModal'
import { useToast } from '@/contexts/ToastContext'
import { adminUserService, AdminUserListPagination } from '@/services/adminUserService'
import { User, UserRole, UserStatus } from '@/types/user'

const ROLE_OPTIONS: Array<{ label: string; value: UserRole | 'all' }> = [
  { label: 'All roles', value: 'all' },
  { label: 'Mentor', value: UserRole.MENTOR },
  { label: 'Mentee', value: UserRole.MENTEE },
  { label: 'Admin', value: UserRole.ADMIN }
]

const ASSIGNABLE_ROLES: UserRole[] = [UserRole.MENTOR, UserRole.MENTEE, UserRole.ADMIN]

const STATUS_OPTIONS: Array<{ label: string; value: UserStatus | 'all' }> = [
  { label: 'All statuses', value: 'all' },
  { label: 'Active', value: UserStatus.ACTIVE },
  { label: 'Inactive', value: UserStatus.INACTIVE },
  { label: 'Pending verification', value: UserStatus.PENDING_VERIFICATION },
  { label: 'Suspended', value: UserStatus.SUSPENDED },
  { label: 'Deactivated', value: UserStatus.DEACTIVATED }
]

const AdminUsersPage: React.FC = () => {
  const { showToast } = useToast()

  const [users, setUsers] = useState<User[]>([])
  const [pagination, setPagination] = useState<AdminUserListPagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'all'>('all')
  const [page, setPage] = useState(1)
  const [actionTarget, setActionTarget] = useState<
    { user: User; action: 'suspend' | 'reactivate' | 'role-change'; newRole?: UserRole } | null
  >(null)
  const [isSubmittingAction, setIsSubmittingAction] = useState(false)
  const [pendingRoles, setPendingRoles] = useState<Record<string, UserRole>>({})

  const fetchingRef = useRef(false)

  const fetchUsers = useCallback(async () => {
    if (fetchingRef.current) return

    try {
      fetchingRef.current = true
      setLoading(true)
      const result = await adminUserService.listUsers({
        search: searchQuery || undefined,
        role: roleFilter === 'all' ? undefined : roleFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        page,
        limit: 20
      })
      setUsers(result.users)
      setPagination(result.pagination)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch users'
      })
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [searchQuery, roleFilter, statusFilter, page, showToast])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    setPage(1)
  }, [searchQuery, roleFilter, statusFilter])

  const handleActionConfirm = async (reason: string) => {
    if (!actionTarget) return

    try {
      setIsSubmittingAction(true)
      if (actionTarget.action === 'suspend') {
        await adminUserService.suspendUser(actionTarget.user.id, reason)
        showToast({ type: 'success', message: `${actionTarget.user.firstName} ${actionTarget.user.lastName} has been suspended.` })
      } else if (actionTarget.action === 'reactivate') {
        await adminUserService.reactivateUser(actionTarget.user.id, reason)
        showToast({ type: 'success', message: `${actionTarget.user.firstName} ${actionTarget.user.lastName} has been reactivated.` })
      } else if (actionTarget.newRole) {
        await adminUserService.changeUserRole(actionTarget.user.id, actionTarget.newRole, reason)
        showToast({
          type: 'success',
          message: `${actionTarget.user.firstName} ${actionTarget.user.lastName}'s role has been changed to ${actionTarget.newRole}.`
        })
        setPendingRoles((prev) => {
          const next = { ...prev }
          delete next[actionTarget.user.id]
          return next
        })
      }
      setActionTarget(null)
      await fetchUsers()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update user'
      })
    } finally {
      setIsSubmittingAction(false)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900">Users</h2>
      <p className="mt-2 text-gray-600">Search and manage all users on the platform.</p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as UserStatus | 'all')}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <Card className="mt-6">
        <CardBody>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No users found.</p>
          ) : (
            <>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 text-sm text-gray-500">
                    <th className="pb-2">Name</th>
                    <th className="pb-2">Email</th>
                    <th className="pb-2">Role</th>
                    <th className="pb-2">Status</th>
                    <th className="pb-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 last:border-0">
                      <td className="py-3">
                        <Link
                          to={`/admin/users/${user.id}`}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {user.firstName} {user.lastName}
                        </Link>
                      </td>
                      <td className="py-3 text-gray-600">{user.email}</td>
                      <td className="py-3 text-gray-600 capitalize">{user.role}</td>
                      <td className="py-3 text-gray-600 capitalize">{user.status || 'active'}</td>
                      <td className="py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {user.status === UserStatus.SUSPENDED ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setActionTarget({ user, action: 'reactivate' })}
                            >
                              Reactivate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => setActionTarget({ user, action: 'suspend' })}
                            >
                              Suspend
                            </Button>
                          )}

                          <select
                            aria-label={`New role for ${user.firstName} ${user.lastName}`}
                            value={pendingRoles[user.id] ?? user.role}
                            onChange={(e) =>
                              setPendingRoles((prev) => ({ ...prev, [user.id]: e.target.value as UserRole }))
                            }
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            {ASSIGNABLE_ROLES.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={(pendingRoles[user.id] ?? user.role) === user.role}
                            onClick={() =>
                              setActionTarget({ user, action: 'role-change', newRole: pendingRoles[user.id] ?? user.role })
                            }
                          >
                            Change Role
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <span className="text-sm text-gray-500">
                    Page {pagination.page} of {pagination.totalPages} ({pagination.totalDocs} users)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={!pagination.hasPrevPage}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <ReasonPromptModal
        isOpen={actionTarget !== null}
        title={
          actionTarget?.action === 'suspend'
            ? 'Suspend User'
            : actionTarget?.action === 'reactivate'
              ? 'Reactivate User'
              : 'Change Role'
        }
        description={
          actionTarget
            ? actionTarget.action === 'role-change'
              ? `Changing ${actionTarget.user.firstName} ${actionTarget.user.lastName}'s role from ${actionTarget.user.role} to ${actionTarget.newRole} requires a reason for the audit log.`
              : `${actionTarget.action === 'suspend' ? 'Suspending' : 'Reactivating'} ${actionTarget.user.firstName} ${actionTarget.user.lastName} requires a reason for the audit log.`
            : undefined
        }
        confirmLabel={
          actionTarget?.action === 'suspend' ? 'Suspend' : actionTarget?.action === 'reactivate' ? 'Reactivate' : 'Change Role'
        }
        confirmVariant={actionTarget?.action === 'suspend' ? 'danger' : 'primary'}
        onClose={() => setActionTarget(null)}
        onConfirm={handleActionConfirm}
        isSubmitting={isSubmittingAction}
      />
    </div>
  )
}

export default AdminUsersPage
